/**
 * ArgoActivityFeed — the Home "Argo has been working" section.
 *
 * Renders the newest ~8 entries from the agent activity feed
 * (src/lib/agent/activityFeed.ts): one line per REAL agent run — who ran,
 * when (relative time), and what actually happened ("scanned N records,
 * found K issues, queued M proposals"). Rows that queued proposals link
 * to the Approvals inbox, because a queued proposal is waiting on YOU.
 *
 * Honesty contract: the feed's only writer is `runAgentOnce`, so every
 * line here is a run that actually happened with its real results — and
 * the empty state says so plainly ("No runs yet today.") instead of
 * faking liveliness.
 */

import { useEffect, useState } from 'react';
import { ArrowUpRight, CalendarClock, History, Play } from 'lucide-react';
import { GlassPanel } from './GlassPanel';
import { C } from '../lib/tokens';
import {
  FEED_LIMIT,
  feedRelativeTime,
  subscribeFeed,
  type AgentFeedEntry,
} from '../lib/agent/activityFeed';

export interface ArgoActivityFeedProps {
  /** Jump to the Approvals inbox — shown on rows that queued proposals. */
  onOpenApprovals?: () => void;
}

export function ArgoActivityFeed({ onOpenApprovals }: ArgoActivityFeedProps) {
  const [entries, setEntries] = useState<AgentFeedEntry[]>([]);
  useEffect(() => subscribeFeed(setEntries), []);

  const latest = entries.slice(0, FEED_LIMIT);

  return (
    <GlassPanel
      variant="subtle"
      animate={false}
      style={{ padding: '16px 20px' }}
      aria-label="Argo has been working"
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <span
            aria-hidden="true"
            style={{
              width: 32,
              height: 32,
              borderRadius: 10,
              display: 'inline-grid',
              placeItems: 'center',
              flex: '0 0 auto',
              background: 'linear-gradient(135deg, rgba(255,0,255,0.22), rgba(187,65,152,0.12))',
              border: '1px solid rgba(255,255,255,0.14)',
              color: '#fff',
            }}
          >
            <History size={16} strokeWidth={1.9} />
          </span>
          <div style={{ minWidth: 0 }}>
            <span className="t-eyebrow">Activity</span>
            <h2 className="t-section-title" style={{ margin: '2px 0 0' }}>
              Argo has been working
            </h2>
          </div>
        </div>
        <span className="t-meta" style={{ color: C.textDim }}>
          runs on schedule, proposals still need your approval
        </span>
      </div>

      {latest.length === 0 ? (
        // Honest empty state — the feed only ever contains real runs.
        <div className="t-meta" style={{ marginTop: 12, color: C.textMuted }}>
          No runs yet today.
        </div>
      ) : (
        <ul style={{ listStyle: 'none', margin: '12px 0 0', padding: 0, display: 'grid', gap: 8 }}>
          {latest.map((e) => (
            <li
              key={e.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                flexWrap: 'wrap',
                padding: '8px 12px',
                borderRadius: 10,
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.07)',
              }}
            >
              <span
                aria-hidden="true"
                title={e.trigger === 'schedule' ? 'Ran on its live schedule' : 'Run manually'}
                style={{ color: C.textDim, display: 'inline-flex', flex: '0 0 auto' }}
              >
                {e.trigger === 'schedule' ? <CalendarClock size={13} /> : <Play size={13} />}
              </span>
              <span className="t-card-title" style={{ fontSize: 12.5, flex: '0 0 auto' }}>
                {e.agentName}
              </span>
              <span className="t-meta" style={{ color: C.textMuted, minWidth: 0 }}>
                {e.summary}
              </span>
              <span style={{ flex: 1 }} />
              {e.proposalsQueued > 0 && onOpenApprovals && (
                <button
                  type="button"
                  className="glass-btn"
                  onClick={onOpenApprovals}
                  title="These proposals are waiting for your approval — nothing was written"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                >
                  Review in Approvals <ArrowUpRight size={11} aria-hidden="true" />
                </button>
              )}
              <span className="t-meta" style={{ color: C.textDim, flex: '0 0 auto' }} title={e.ts}>
                {feedRelativeTime(e.ts)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </GlassPanel>
  );
}
